import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { useConfirm } from '../context/ConfirmContext';
import {
  buildCustomerPayload,
  customerFormFromRecord,
  emptyCustomerForm
} from '../utils/customerForm';
import './Tasks.css';
import './Customers.css';

const Customers = () => {
  const { t } = useTranslation(['customers', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyCustomerForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const companyId = user?.active_company_id || user?.company_id;

  useEffect(() => {
    fetchCustomers({ initial: true });
  }, [companyId]);

  const fetchCustomers = async ({ initial = false } = {}) => {
    try {
      if (initial) {
        setLoading(true);
      }
      setError('');
      const response = await axios.get('/api/customers');
      setCustomers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(formatError(err) || t('customers:failedFetch'));
    } finally {
      if (initial) {
        setLoading(false);
      }
    }
  };

  const handleOpenCreate = () => {
    setEditingCustomer(null);
    setFormData(emptyCustomerForm);
    setError('');
    setShowModal(true);
  };

  const handleOpenEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData(customerFormFromRecord(customer));
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setFormData(emptyCustomerForm);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    const payload = buildCustomerPayload(formData);

    try {
      if (editingCustomer) {
        await axios.put(`/api/customers/${editingCustomer.id}`, payload);
      } else {
        await axios.post('/api/customers', payload);
      }
      handleCloseModal();
      await fetchCustomers();
    } catch (err) {
      setError(formatError(err) || t('customers:failedSave'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingCustomer) {
      return;
    }
    if (!(await confirm(t('customers:confirmDelete', { name: editingCustomer.name }), { danger: true }))) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.delete(`/api/customers/${editingCustomer.id}`);
      handleCloseModal();
      await fetchCustomers();
    } catch (err) {
      setError(formatError(err) || t('customers:failedDelete'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="customers-page">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('customers:pageTitle')}</h1>
          <button type="button" className="btn btn-primary tasks-header-create-btn" onClick={handleOpenCreate}>
            <span className="material-symbols-outlined" aria-hidden="true">add</span>
            {t('customers:createCustomer')}
          </button>
        </div>

        {error && !showModal && <div className="error">{error}</div>}

        {customers.length > 0 ? (
          <section className="tasks-section">
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    <th>{t('common:name')}</th>
                    <th>{t('customers:organization')}</th>
                    <th>{t('customers:email')}</th>
                    <th>{t('customers:phone')}</th>
                    <th>{t('customers:address')}</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="tasks-list-table-row"
                      onClick={() => handleOpenEdit(customer)}
                    >
                      <td className="tasks-list-table-title">{customer.name}</td>
                      <td>{customer.organization_name || t('common:emptyCell')}</td>
                      <td>{customer.email || t('common:emptyCell')}</td>
                      <td>{customer.phone || t('common:emptyCell')}</td>
                      <td>{customer.address || t('common:emptyCell')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tasks-card-grid tasks-list-cards-mobile">
              {customers.map((customer) => (
                <article
                  key={customer.id}
                  className="tasks-redesign-card"
                  onClick={() => handleOpenEdit(customer)}
                >
                  <div className="tasks-redesign-card-body">
                    <div className="tasks-redesign-card-top">
                      <h3 className="tasks-redesign-card-title">{customer.name}</h3>
                    </div>
                    <div className="tasks-redesign-card-meta">
                      {customer.organization_name && (
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">apartment</span>
                          {customer.organization_name}
                        </span>
                      )}
                      {customer.email && (
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">mail</span>
                          {customer.email}
                        </span>
                      )}
                      {customer.phone && (
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">call</span>
                          {customer.phone}
                        </span>
                      )}
                      {customer.address && (
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">location_on</span>
                          {customer.address}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : !error ? (
          <EmptyState
            icon="contacts"
            title={t('customers:noCustomersTitle')}
            description={t('customers:noCustomersDescription')}
            actionLabel={t('customers:createCustomer')}
            onAction={handleOpenCreate}
          />
        ) : null}

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingCustomer ? t('customers:editCustomer') : t('customers:createCustomer')}
        >
          {error && <div className="error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="customer-name">{t('customers:nameRequired')}</label>
              <input
                id="customer-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                maxLength={255}
              />
            </div>
            <div className="form-group">
              <label htmlFor="customer-organization">{t('customers:organization')}</label>
              <input
                id="customer-organization"
                type="text"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleChange}
                maxLength={255}
              />
            </div>
            <div className="form-group">
              <label htmlFor="customer-email">{t('customers:email')}</label>
              <input
                id="customer-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                maxLength={255}
              />
            </div>
            <div className="form-group">
              <label htmlFor="customer-phone">{t('customers:phone')}</label>
              <input
                id="customer-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                maxLength={50}
              />
            </div>
            <div className="form-group">
              <label htmlFor="customer-address">{t('customers:address')}</label>
              <textarea
                id="customer-address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
              />
            </div>
            <div className="modal-actions">
              {editingCustomer && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={submitting}
                >
                  {t('common:delete')}
                </button>
              )}
              <div className="modal-actions-right">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  {t('common:cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting
                    ? t('common:saving')
                    : editingCustomer
                      ? t('common:save')
                      : t('common:create')}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default Customers;
